import type { Bruker } from '@klp/user';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import type { MUIDataTableColumn } from 'mui-datatables';
import { getConfig } from '@klpkonsern/appconfig';

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
    return typeof error === 'object' && error !== null && 'status' in error;
}

function isSerializedError(error: unknown): error is SerializedError {
    return typeof error === 'object' && error !== null && 'message' in error;
}

const handleFormikNumericFieldFocus = (
    event: React.FocusEvent<HTMLInputElement>,
    defaultVal: number,
    handleChangeCallback:
        | ((e: React.ChangeEvent<HTMLInputElement>) => void)
        | undefined,
) => {
    if (event.target.value === String(defaultVal)) {
        event.target.value = '';
        handleChangeCallback?.(event);
    }
};

const handleFormikNumericFieldBlur = (
    event: React.FocusEvent<HTMLInputElement>,
    handleChangeCallback:
        | ((e: React.ChangeEvent<HTMLInputElement>) => void)
        | undefined,
) => {
    if (
        event.target.value === null ||
        event.target.value === '' ||
        event.target.value === undefined
    ) {
        event.target.value = '0';
        handleChangeCallback?.(event);
    }
};

const handleFormikNumericFieldChange = (
    handleChangeCallback: (e: React.ChangeEvent<HTMLInputElement>) => void,
    event: React.ChangeEvent<any>,
    name: string,
) => {
    let eventRef = event;

    if (typeof event.target.value === 'string') {
        const val = parseFloat(event.target.value || '0');

        const modifiedEvent = {
            ...event,
            target: {
                ...event.target,
                value: val,
                id: event.target.id || name,
            },
        };

        eventRef = modifiedEvent;
    }

    handleChangeCallback(eventRef);
};

function applyFilterList(
    base: Array<MUIDataTableColumn>,
    filterList?: Array<Array<string>>,
): Array<MUIDataTableColumn> {
    return base.map((col, idx) => ({
        ...col,
        options: {
            ...col.options,
            filterList: filterList?.[idx] ?? [],
        },
    }));
}

const regionAccess = (
    currentUser: Bruker | undefined,
    moduleRegionConfig?: string,
): boolean => {
    if (!currentUser) return false;

    // Use module-specific config if provided, otherwise fall back to global config
    const enabledRegions =
        moduleRegionConfig ?? getConfig('WEBAPP_REGION_ENABLE', '');

    // Empty string means enabled for all regions
    if (enabledRegions === '') return true;

    // User has no region specified or has specified "Alle" - allow access
    if (!currentUser?.region || currentUser?.region === 'Alle') return true;

    // Parse enabled regions and check if user's region is in the list
    const enabledRegionsList = enabledRegions
        .split(',')
        .map((region) => region.trim())
        .filter((region) => region.length > 0);

    return enabledRegionsList.includes(currentUser?.region);
};

export {
    isFetchBaseQueryError,
    isSerializedError,
    handleFormikNumericFieldBlur,
    handleFormikNumericFieldFocus,
    handleFormikNumericFieldChange,
    applyFilterList,
    regionAccess,
};
